import { startSessionLifecycle } from '../platform/sessionLifecycle';
// The signed-in account, kept in memory only: the session itself is an HttpOnly cookie, so
// every launch asks the server (/api/me). Inside Telegram, launching signs in with initData.
// Recovery codes pass through here once (`codes`) and are dropped as soon as they are saved.
import { create } from 'zustand';
import i18n from '../i18n';
import type { Strings } from '../i18n/en';
import type { AccountUser } from '../types';
import * as auth from '../platform/auth';
import { telegram } from '../platform/telegram';

export type AccountError = keyof Strings['account']['errors'];

type Patch = Partial<Pick<AccountState, 'user' | 'codes' | 'recovered'>>;

type AccountState = {
  status: 'loading' | 'ready';
  user: AccountUser | null;
  busy: boolean;
  /** While signing up: the anti-abuse check, then the passkey prompt. */
  phase: 'idle' | 'check' | 'passkey';
  /** Key under account.errors, or null. */
  error: AccountError | null;
  /** When the server said to wait: the moment (ms epoch) the user may try again. Drives live countdowns. */
  retryUntil: number | null;
  /** Freshly made recovery codes, shown once. Never persisted. */
  codes: string[] | null;
  /** Signed in with a recovery code: nudge the user to add a passkey. */
  recovered: boolean;
  init: () => Promise<void>;
  /** Runs a call; `reauth` signs in again first if the server wants a recent sign-in. */
  run: (action: () => Promise<Patch | AccountUser | void>, reauth?: boolean) => Promise<void>;
  dismissCodes: () => void;
  clearError: () => void;
};

export const errorKey = (e: unknown): AccountError | null => {
  const name = (e as { name?: string })?.name;
  // Inside Telegram a failed passkey prompt is almost always Telegram's browser, and "cancelled" cannot be
  // told from "blocked" there: say so honestly instead of staying silent.
  if (telegram.isInside && !(e instanceof auth.ApiError)) return 'passkey_in_telegram';
  if (name === 'NotAllowedError') return null; // prompt dismissed
  if (name === 'InvalidStateError') return 'already_registered'; // this device already has a passkey for the account
  const code = e instanceof auth.ApiError ? e.code : 'generic';
  return i18n.exists(`account.errors.${code}`) ? (code as AccountError) : 'generic';
};

const isUser = (v: unknown): v is AccountUser => !!v && typeof v === 'object' && 'passkeys' in v;

/** Sensitive actions need a sign-in from the last 15 minutes; do it for the user, then retry once. */
export async function withReauth<T>(action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (e) {
    if (!(e instanceof auth.ApiError) || e.code !== 'reauth_required') throw e;
    if (telegram.isInside) await auth.telegramSignIn(telegram.initData);
    else await auth.signIn();
    return action();
  }
}

export const useAccount = create<AccountState>((set, get) => ({
  status: 'loading',
  user: null,
  busy: false,
  phase: 'idle',
  error: null,
  retryUntil: null,
  codes: null,
  recovered: false,
  async init() {
    if (!auth.authAvailable) return set({ status: 'ready' });
    try {
      const active=await startSessionLifecycle(user=>{const revoked=!!get().user&&!user;set({user,...(revoked?{error:'session_active' as const}:user?{error:null}:{})});},()=>set({user:null,error:'session_active',status:'ready'}));
      if (!active) return set({status:'ready'});
      set({error:null});
      if (telegram.isInside) return set({ user: await auth.telegramSignIn(telegram.initData), status: 'ready' });
      set({ user: await auth.fetchMe(), status: 'ready' });
    } catch (e) {
      set({ status: 'ready', error: e instanceof auth.ApiError && e.code === 'telegram_not_configured' ? null : errorKey(e) });
    }
  },
  async run(action, reauth = false) {
    if (get().busy) return;
    set({ busy: true, error: null, retryUntil: null });
    try {
      const result = await (reauth ? withReauth(action) : action());
      if (isUser(result)) set({ user: result });
      else if (result) set(result);
    } catch (e) {
      const wait = e instanceof auth.ApiError ? e.retryAfter : undefined;
      set({ error: errorKey(e), retryUntil: wait ? Date.now() + wait * 1000 : null });
    } finally {
      set({ busy: false, phase: 'idle' });
    }
  },
  dismissCodes: () => set({ codes: null }),
  clearError: () => set({ error: null, retryUntil: null }),
}));
